import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Bell, Clock, X } from 'lucide-react';
import { useMedicalReminderPreferences } from '../hooks/useMedicalReminderPreferences';
import { useToast } from '@/hooks/use-toast';

const MedicalReminderSettings = () => {
  const { preferences, isLoading, updatePreferences } = useMedicalReminderPreferences();
  const { toast } = useToast();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Reminder Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!preferences) return null;

  const handleToggle = async (field: 'appointment_reminders_enabled' | 'vaccination_reminders_enabled' | 'birthday_reminders_enabled', value: boolean) => {
    const result = await updatePreferences({ [field]: value });
    if (result?.success) {
      toast({
        title: "Settings updated",
        description: "Your reminder preferences have been saved.",
      });
    } else {
      toast({
        title: "Error",
        description: "Failed to update reminder settings.",
        variant: "destructive",
      });
    }
  };

  const handleReminderDaysChange = (days: number[]) => {
    updatePreferences({ reminder_days_before: days });
  };

  const addReminderDay = (day: number) => {
    if (!preferences.reminder_days_before.includes(day)) {
      const newDays = [...preferences.reminder_days_before, day].sort((a, b) => a - b);
      handleReminderDaysChange(newDays);
    }
  };

  const removeReminderDay = (day: number) => {
    const newDays = preferences.reminder_days_before.filter(d => d !== day);
    handleReminderDaysChange(newDays);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          Reminder Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Appointment Reminders */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="appointment-reminders" className="font-medium">
              Appointment Reminders
            </Label>
            <p className="text-sm text-muted-foreground">
              Get notified before upcoming appointments
            </p>
          </div>
          <Switch
            id="appointment-reminders"
            checked={preferences.appointment_reminders_enabled}
            onCheckedChange={(checked) => handleToggle('appointment_reminders_enabled', checked)}
          />
        </div>

        {/* Vaccination Reminders */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="vaccination-reminders" className="font-medium">
              Vaccination Reminders
            </Label>
            <p className="text-sm text-muted-foreground">
              Get notified about due vaccinations
            </p>
          </div>
          <Switch
            id="vaccination-reminders"
            checked={preferences.vaccination_reminders_enabled}
            onCheckedChange={(checked) => handleToggle('vaccination_reminders_enabled', checked)}
          />
        </div>

        {/* Birthday Reminders */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="birthday-reminders" className="font-medium">
              Pet Birthday Reminders
            </Label>
            <p className="text-sm text-muted-foreground">
              Get notified on your pet's birthday
            </p>
          </div>
          <Switch
            id="birthday-reminders"
            checked={preferences.birthday_reminders_enabled ?? true}
            onCheckedChange={(checked) => handleToggle('birthday_reminders_enabled', checked)}
          />
        </div>

        {/* Reminder Timing */}
        {(preferences.appointment_reminders_enabled || preferences.vaccination_reminders_enabled) && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <Label className="font-medium">Remind me</Label>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {preferences.reminder_days_before.map((days) => (
                <Badge key={days} variant="secondary" className="flex items-center gap-1">
                  {days === 0 ? 'Same day' : days === 1 ? '1 day before' : `${days} days before`}
                  <button
                    onClick={() => removeReminderDay(days)}
                    className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>

            <div className="flex gap-2 flex-wrap">
              {[0, 1, 2, 3, 7, 14].map((day) => (
                !preferences.reminder_days_before.includes(day) && (
                  <Button
                    key={day}
                    variant="outline"
                    size="sm"
                    onClick={() => addReminderDay(day)}
                    className="text-xs"
                  >
                    + {day === 0 ? 'Same day' : day === 1 ? '1 day' : `${day} days`}
                  </Button>
                )
              ))}
            </div>
          </div>
        )}

        <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
          <p className="font-medium mb-1">How it works:</p>
          <p>We'll check daily at 9 AM and send notifications for upcoming appointments and due vaccinations based on your preferences.</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default MedicalReminderSettings;